import { useState, useCallback, useMemo } from "react";
import { useAssignOrdersMutation } from "@/shared/api/lod-api";
import toast from "react-hot-toast";
import { Order } from "@/shared/api/lod-api";

export function useAssignmentActions(
  rowSelection: Record<string, boolean>,
  tableData: Order[],
  onAssignmentSuccess?: () => void
) {
  const [selectedAssignee, setSelectedAssignee] = useState<
    string | undefined
  >();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ordersWithAssignee, setOrdersWithAssignee] = useState(0);

  const [assignOrders, { isLoading: isAssigning }] = useAssignOrdersMutation();

  const getSelectedOrderIds = useCallback(() => {
    return Object.keys(rowSelection).filter((orderId) => rowSelection[orderId]);
  }, [rowSelection]);

  const getSelectedOrders = useCallback(() => {
    const orderIdMap = new Map<string, Order>();
    tableData.forEach((order) => {
      orderIdMap.set(order.id, order);
    });

    return Object.keys(rowSelection)
      .filter((orderId) => rowSelection[orderId] && orderIdMap.has(orderId))
      .map((orderId) => orderIdMap.get(orderId)!);
  }, [rowSelection, tableData]);

  const countOrdersWithAssignee = useCallback(() => {
    return getSelectedOrders().filter((order) => order.annotator_id).length;
  }, [getSelectedOrders]);

  const selectedOrdersCSV = useMemo(() => {
    const selectedOrders = getSelectedOrders();

    return selectedOrders
      .map((order) => `"${order.time}", "${order.id}", "${order.order_source}"`)
      .join("\n");
  }, [getSelectedOrders]);

  const handleAssign = useCallback(async () => {
    if (!selectedAssignee) {
      toast.error("Please select an assignee");
      return;
    }

    const selectedOrderIds = getSelectedOrderIds();

    if (selectedOrderIds.length === 0) {
      toast.error("Please select at least one order");
      return;
    }

    const ordersWithAssigneeCount = countOrdersWithAssignee();

    if (ordersWithAssigneeCount > 0) {
      setOrdersWithAssignee(ordersWithAssigneeCount);
      setDialogOpen(true);
      return;
    }

    await performAssignment();
  }, [selectedAssignee, getSelectedOrderIds, countOrdersWithAssignee]);

  const performAssignment = useCallback(async () => {
    try {
      if (!selectedAssignee) {
        toast.error("Please select an assignee");
        return;
      }

      const selectedOrderIds = getSelectedOrderIds();

      await assignOrders({
        order_ids: selectedOrderIds,
        annotator_id: selectedAssignee === "null" ? null : selectedAssignee,
      }).unwrap();

      toast.success("Orders assigned successfully");
      onAssignmentSuccess?.();
      return true;
    } catch (error) {
      toast.error("Failed to assign orders");
      console.error(error);
      return false;
    }
  }, [
    selectedAssignee,
    getSelectedOrderIds,
    assignOrders,
    onAssignmentSuccess,
  ]);

  const handleConfirmAssignment = useCallback(() => {
    performAssignment();
    setDialogOpen(false);
  }, [performAssignment]);

  return {
    assignmentState: {
      selectedAssignee,
      dialogOpen,
      ordersWithAssignee,
      isAssigning,
      selectedOrdersCSV,
    },

    assignmentActions: {
      setSelectedAssignee,
      setDialogOpen,
      handleAssign,
      handleConfirmAssignment,
      performAssignment,
    },
  };
}
